import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { InfluencerSubmission, SubmissionStatus } from "@/lib/interfaces";
import { Check, X, Edit2, Calendar, Tag } from "lucide-react";

interface SubmissionDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  submission: InfluencerSubmission | null;
  onApprove: (id: number) => void;
  onReject: (id: number, feedback: string) => void;
}

const statusColors: Record<SubmissionStatus, string> = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
  approved: "bg-green-100 text-green-800 border-green-300",
  rejected: "bg-red-100 text-red-800 border-red-300",
};

export default function SubmissionDetailDialog({
  open,
  onOpenChange,
  submission,
  onApprove,
  onReject,
}: SubmissionDetailDialogProps) {
  const [feedback, setFeedback] = React.useState("");
  const [showRejectForm, setShowRejectForm] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setFeedback("");
      setShowRejectForm(false);
    }
  }, [open]);

  if (!submission) return null;

  const isPending = submission.status === "pending";
  const media = submission.media || [];
  const tags = submission.tags || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-full !max-w-[98vw] h-[95vh] flex flex-col p-8">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-2xl">
            Submission Details
            <span
              className={`px-3 py-1.5 rounded-full text-sm font-medium border ${
                statusColors[submission.status]
              }`}
            >
              {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
            </span>
            {submission.edited_by_admin && (
              <span className="px-3 py-1.5 rounded-full text-sm font-medium border bg-blue-100 text-blue-800 border-blue-300 flex items-center gap-1">
                <Edit2 className="h-3 w-3" />
                Edited
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Review the submission details and media content below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-8 py-6 flex-1 overflow-y-auto">
          {/* Submission Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <Label className="text-muted-foreground text-sm">Platform</Label>
              <p className="font-medium text-lg mt-1 capitalize">{submission.platform}</p>
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">Influencer</Label>
              <p className="font-medium text-lg mt-1">
                {submission.influencer?.username || submission.influencer?.email || `#${submission.influencer_id}`}
              </p>
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">Submitted At</Label>
              <p className="font-medium text-lg mt-1">
                {new Date(submission.created_at).toLocaleString()}
              </p>
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">Expected Publication</Label>
              <p className="font-medium text-lg mt-1 flex items-center gap-1">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                {submission.expected_publication_date
                  ? new Date(submission.expected_publication_date).toLocaleDateString()
                  : "Not specified"}
              </p>
            </div>
          </div>

          {/* Caption */}
          <div>
            <Label className="text-muted-foreground text-sm">Caption</Label>
            <p className="mt-2 whitespace-pre-wrap text-base bg-gray-50 p-4 rounded-lg border">
              {submission.caption}
            </p>
          </div>

          {/* Tags */}
          {tags.length > 0 && (
            <div>
              <Label className="text-muted-foreground text-sm flex items-center gap-1">
                <Tag className="h-4 w-4" /> Tags
              </Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Objective */}
          {submission.objective && (
            <div>
              <Label className="text-muted-foreground text-sm">Objective</Label>
              <p className="mt-2 whitespace-pre-wrap text-base">{submission.objective}</p>
            </div>
          )}

          {/* Media Gallery */}
          <div>
            <Label className="text-muted-foreground text-sm">Media ({media.length} items)</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-4">
              {media.map((item, index) => (
                <div
                  key={index}
                  className="relative aspect-square rounded-xl overflow-hidden border-2 bg-muted shadow-sm hover:shadow-md transition-shadow"
                >
                  {item.type === "video" || item.type === "reel" ? (
                    <video
                      src={item.url}
                      controls
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img
                      src={item.url}
                      alt={`Media ${index + 1}`}
                      className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                      onClick={() => window.open(item.url, '_blank')}
                    />
                  )}
                  <span className="absolute top-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded-full capitalize">
                    {item.type}
                  </span>
                </div>
              ))}
              {media.length === 0 && (
                <p className="col-span-full text-muted-foreground text-center py-12 text-lg">
                  No media attached
                </p>
              )}
            </div>
          </div>

          {/* Admin Feedback (if rejected) */}
          {submission.status === "rejected" && submission.admin_feedback && (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
              <Label className="text-red-700 text-sm font-medium">Rejection Feedback</Label>
              <p className="mt-2 text-red-800 text-base">{submission.admin_feedback}</p>
            </div>
          )}

          {/* Reject Form */}
          {isPending && showRejectForm && (
            <div className="bg-gray-50 border-2 rounded-xl p-6 space-y-4">
              <Label className="text-base font-medium">
                Rejection Feedback <span className="text-red-500">*</span>
              </Label>
              <Textarea
                placeholder="Provide feedback for rejection (required)..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={4}
                className="text-base"
              />
              <div className="flex gap-3">
                <Button
                  variant="destructive"
                  size="lg"
                  onClick={() => {
                    if (feedback.trim()) {
                      onReject(submission.id, feedback.trim());
                      onOpenChange(false);
                    }
                  }}
                  disabled={!feedback.trim()}
                >
                  Confirm Rejection
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setShowRejectForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        {isPending && !showRejectForm && (
          <DialogFooter className="gap-3 pt-4 border-t">
            <Button
              variant="outline"
              size="lg"
              onClick={() => setShowRejectForm(true)}
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              <X className="mr-2 h-5 w-5" />
              Reject
            </Button>
            <Button
              size="lg"
              onClick={() => {
                onApprove(submission.id);
                onOpenChange(false);
              }}
              className="bg-green-600 hover:bg-green-700"
            >
              <Check className="mr-2 h-5 w-5" />
              Approve
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
